import * as React from 'react';

type UseHoverInstantMode = {
    shouldShowInstantly: boolean;
    handleChildHoverDisplayChange: (isShown: boolean) => void;
};

/**
 * Hook used to determine if any elements are currently hovered.
 * If any elements are hovered, show the hover display instantly
 * when hovering any other element.
 */
export function useDetermineHoverInstantMode(offDelayMs = 200): UseHoverInstantMode {
    const [shouldShowInstantly, setIsAnyInstant] = React.useState<boolean>(false);
    const isAnyShownRef = React.useRef<boolean>(false);
    const offTimerRef = React.useRef<number | null | undefined>(null);

    const handleChildHoverDisplayChange = React.useCallback(
        (isShown: boolean) => {
            if (isShown) {
                if (offTimerRef.current) {
                    clearTimeout(offTimerRef.current);
                    offTimerRef.current = null;
                }
                if (!isAnyShownRef.current) {
                    setIsAnyInstant(true);
                }
                isAnyShownRef.current = true;

                return;
            }

            if (!isAnyShownRef.current) {
                return;
            }
            isAnyShownRef.current = false;

            if (offTimerRef.current) {
                clearTimeout(offTimerRef.current);
            }
            // @ts-expect-error
            offTimerRef.current = setTimeout(() => {
                if (!isAnyShownRef.current) {
                    setIsAnyInstant(false);
                }
                offTimerRef.current = null;
            }, offDelayMs);
        },
        [offDelayMs]
    );

    return {shouldShowInstantly, handleChildHoverDisplayChange};
}
